import styled from "@emotion/styled";
import type {
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  AutocompleteProps as MuiAutocompleteProps,
} from "@mui/material";
import { FormControl, FormHelperText, Autocomplete as MuiAutocomplete, TextField, Tooltip } from "@mui/material";
import { useField } from "formik";
import type { ReactNode } from "react";
import { useMemo } from "react";

import { Icon } from "../../icons";
import { FONT_WEIGHT_SEMI_BOLD } from "../../utilities/constants";
import { useFieldContext } from "../FormFieldContext";
import { useCommitChange, useFieldLabelAndDescription, useFieldPlaceholder } from "./helpers";
import { TagChip } from "./TagChip";
import type { AutocompleteOption } from "./types";

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
  margin-bottom: 4px;
  gap: 6px;
  overflow-y: auto;
  max-height: 106px;

  > * {
    max-width: calc(50% - 3px);
  }
`;

const GroupHeader = styled.div`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  padding: 6px 16px 2px;
`;

const GroupItems = styled.ul`
  padding: 0;
`;

// Ungrouped options ("" group) render without a header; expects options contiguous by group.
const renderOptionGroup = (params: AutocompleteRenderGroupParams) =>
  params.group ? (
    <li key={params.key}>
      <GroupHeader>{params.group}</GroupHeader>
      <GroupItems>{params.children}</GroupItems>
    </li>
  ) : (
    <li key={params.key}>
      <GroupItems>{params.children}</GroupItems>
    </li>
  );

export type AutocompleteProps<T extends AutocompleteOption = AutocompleteOption> = Omit<
  MuiAutocompleteProps<T, boolean, boolean, boolean>,
  "renderInput" | "getOptionLabel" | "onChange"
> & {
  options: AutocompleteOption[];
  label?: ReactNode;
  helperText?: string;
  onChange: (values: string[] | string) => void;
  onSelectedOptionChange?: (option: AutocompleteOption) => void;
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
  addValueOfTextInputOnBlur?: boolean;
  placeholder?: string;
  /** Group the dropdown by each option's `group`, with a header per group (none for ungrouped). */
  groupByOptionGroup?: boolean;
};

export const FormAutocomplete = ({
  addValueOfTextInputOnBlur = false,
  autoComplete = true,
  blurOnSelect = true,
  disableClearable = false,
  freeSolo = true,
  groupByOptionGroup = false,
  helperText,
  label,
  multiple = false,
  onChange,
  onSelectedOptionChange,
  openOnFocus = true,
  options,
  ...props
}: AutocompleteProps) => {
  const handleChange: MuiAutocompleteProps<AutocompleteOption, boolean, boolean, boolean>["onChange"] = (
    _event,
    value,
  ) => {
    if (Array.isArray(value)) {
      onChange(value.map((option) => (typeof option === "string" ? option : option.value)));
    } else {
      onChange((value as AutocompleteOption)?.value);
      if (onSelectedOptionChange) {
        const selectedOption = options.find((option) => option.value === (value as AutocompleteOption)?.value);
        selectedOption && onSelectedOptionChange(selectedOption);
      }
    }
  };

  const selectedOptions =
    multiple && Array.isArray(props.value) ? (props.value as (AutocompleteOption | string)[]) : [];

  const handleDelete = (indexToRemove: number) => {
    onChange(
      selectedOptions
        .filter((_, index) => index !== indexToRemove)
        .map((option) => (typeof option === "string" ? option : option.value)),
    );
  };

  const renderOption: NonNullable<AutocompleteProps["renderOption"]> = (optionProps, option) => (
    <li {...optionProps} key={`${option.label}-${option.value}`}>
      {option.label}
    </li>
  );

  return (
    <FormControl>
      <MuiAutocomplete<AutocompleteOption, typeof multiple, typeof disableClearable, typeof freeSolo>
        {...props}
        popupIcon={<Icon icon="angle-down" size={12} />}
        autoComplete={autoComplete}
        blurOnSelect={blurOnSelect}
        disableClearable={multiple || disableClearable} // Disable clear button for multi-select, since the chips have clear buttons.
        freeSolo={freeSolo}
        multiple={multiple}
        onChange={handleChange}
        openOnFocus={openOnFocus}
        options={options}
        groupBy={groupByOptionGroup ? (option: AutocompleteOption) => option.group ?? "" : undefined}
        renderGroup={groupByOptionGroup ? renderOptionGroup : undefined}
        getOptionDisabled={(option) => !!option?.disabled}
        getOptionLabel={(option: AutocompleteOption | string | AutocompleteOption[]) => {
          if (Array.isArray(option) && option[0]) {
            return option[0].label;
          }
          if (typeof option === "string") {
            return option;
          }
          return (option as AutocompleteOption).label ?? "";
        }}
        renderInput={
          props.renderInput ??
          ((params) => (
            <TextField
              {...params}
              placeholder={props.placeholder}
              label={label}
              {...(addValueOfTextInputOnBlur &&
                multiple && {
                  onBlur: (e) => {
                    if (!!e.currentTarget.value.trim()) {
                      onChange([...(props.value as string[]), e.currentTarget.value]);
                    }
                  },
                })}
            />
          ))
        }
        renderOption={props.renderOption ?? renderOption}
        // Multi-select shows its selection as chips below the input, not inside it.
        renderValue={props.renderValue ?? (multiple ? () => null : undefined)}
        isOptionEqualToValue={
          multiple
            ? undefined
            : (option: AutocompleteOption, value: string | AutocompleteOption) =>
                option.value === (typeof value === "string" ? value : value?.value)
        }
      />

      {/* A caller rendering its own value (`renderValue`) owns the whole selection UI. */}
      {!props.renderValue && selectedOptions.length > 0 && (
        <ChipsContainer>
          {selectedOptions.map((option, index) => {
            const chipLabel = typeof option === "string" ? option : option.label;
            return (
              <Tooltip title={chipLabel} key={`${typeof option === "string" ? option : option.value}-${index}`}>
                <TagChip label={chipLabel} onDelete={props.disabled ? undefined : () => handleDelete(index)} />
              </Tooltip>
            );
          })}
        </ChipsContainer>
      )}

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

type FieldAutocompleteProps = Omit<AutocompleteProps, "label" | "helperText" | "onChange">;

export const FieldAutocomplete = ({ options, ...props }: FieldAutocompleteProps) => {
  const commitChange = useCommitChange();

  const { name } = useFieldContext();
  const [label, helperText] = useFieldLabelAndDescription(name);
  const fieldPlaceholder = useFieldPlaceholder(name);

  const [{ value }, _, { setValue }] = useField<string | string[]>(name);

  const handleChange: AutocompleteProps["onChange"] = (values) => {
    setValue(values)
      .then(() => commitChange())
      .catch(console.warn);
  };

  // Mui warns when the value matches no option; offer a disabled empty option instead
  // (without mutating the caller's options array).
  const needsEmptyOption = value === "" && options[0]?.value !== "";

  const displayOptions = useMemo(
    () => (needsEmptyOption ? [{ value: "", label: label ?? "", disabled: true }, ...options] : options),
    [needsEmptyOption, label, options],
  );

  // Convert value to Option - Mui Autocomplete component requires an Option as its value.
  const valueToOption = useMemo(() => {
    if (needsEmptyOption) return [{ value: "", label: "" }];

    return (Array.isArray(value) ? value : [value])
      .map((id) => options.find((option) => option.value === id))
      .filter((option) => !!option) as AutocompleteOption[];
  }, [needsEmptyOption, options, value]);

  return (
    <FormAutocomplete
      {...props}
      options={displayOptions}
      label={label}
      placeholder={props.placeholder ?? fieldPlaceholder}
      helperText={helperText}
      onChange={handleChange}
      value={valueToOption}
    />
  );
};
